import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { createShapeId, type TLShapeId } from "@tldraw/tlschema";
import type { Editor, VecLike } from "./canvas";
import { FileImportQueue } from "./file-import-queue";
import { createFileOutbox } from "./file-outbox";
import type { FileEntry, ImportResult } from "./file-import-state";
import { choicesFor } from "./ImportChoices";
import { imagePlacement } from "./sheet-operations";
import {
  isDxfFile,
  isPdfFile,
  isPreviewImage,
  normalizeBoardFile,
} from "../shared/drawing-files";
import { importEditable } from "./editable-imports";
import { importAttachment } from "./file-imports";
import { importPdf } from "./pdf-imports";
import { importDrawing } from "./documents";
import { requireImportTarget } from "./import-target";
import { imageImports } from "./image-imports";
import { notify, type Room } from "./api";
export function useFileImports(
  editor: Editor,
  room: Room,
  userId: string,
  connected: boolean,
) {
  const [error, setError] = useState("");
  const [lastAdded, setLastAdded] = useState<TLShapeId | null>(null);
  const pickerSlot = useRef({ parent: "", next: 0 });
  const editable = useRef(false);
  editable.current =
    connected &&
    (room.role === "editor" || room.role === "owner") &&
    !editor.getIsReadonly();
  const maxBytes =
    room.platform === "cloudflare" ? 20_000_000 : 30 * 1024 * 1024;
  const queue = useMemo(
    () =>
      new FileImportQueue(createFileOutbox(userId, room.id), {
        exists: (id) => !!editor.getShape(id as TLShapeId),
        editable: () => editable.current,
        repair(entry, result) {
          requireImportTarget(editor, {
            ...entry.placement,
            parentId: result.shape.parentId,
          });
          if (editor.getShape(result.shape.id)) return;
          editor.markHistoryStoppingPoint("Restore file import");
          editor.run(() => {
            editor.createAssets(
              result.assets.filter((a) => !editor.getAsset(a.id)),
            );
            editor.createShapes([result.shape]);
          });
        },
        async process(entry, file, signal) {
          if (file.size > maxBytes)
            throw new Error(
              `This file exceeds ${room.platform === "cloudflare" ? 20 : 30} MB. Remove it and choose a smaller file.`,
            );
          if (!file.size) throw new Error("This file is empty.");
          const placement = requireImportTarget(editor, entry.placement),
            cloud = room.platform === "cloudflare",
            id = entry.id as TLShapeId;
          if (isDxfFile(file) && entry.mode === "drawing") {
            const { importDxfDrawing } = await import("./dxf-imports");
            await importDxfDrawing(editor, room.id, file, signal, cloud, {
              id,
              placement,
            });
          } else if (isPdfFile(file)) {
            if (!Number.isInteger(entry.page) || entry.page < 1)
              throw new Error("Choose a whole PDF page number starting at 1.");
            if (entry.mode === "attachment")
              await importAttachment(
                editor,
                room.id,
                file,
                signal,
                cloud,
                placement,
                id,
              );
            else if (entry.mode === "drawing")
              await importDrawing(
                editor,
                room.id,
                file,
                entry.page,
                signal,
                cloud,
                { id, placement },
              );
            else
              await importPdf(
                editor,
                room.id,
                file,
                signal,
                cloud,
                entry.page,
                placement,
                id,
              );
          } else if (
            entry.mode === "document" ||
            entry.mode === "table" ||
            entry.mode === "grid"
          )
            await importEditable(
              editor,
              room.id,
              file,
              signal,
              cloud,
              entry.mode,
              entry.headers,
              entry.worksheet,
              placement,
              id,
            );
          else
            await importAttachment(
              editor,
              room.id,
              file,
              signal,
              cloud,
              placement,
              id,
            );
          const shape = editor.getShape(id);
          if (!shape) throw new Error("The imported object was removed.");
          setLastAdded(id);
          return {
            shape,
            assets:
              shape.type === "image" && shape.props.assetId
                ? [editor.getAsset(shape.props.assetId)!]
                : [],
          } satisfies ImportResult;
        },
      }),
    [editor, room.id, room.platform, userId],
  );
  const entries = useSyncExternalStore(queue.subscribe, queue.getSnapshot);
  const restored = useRef<FileImportQueue | null>(null);
  useEffect(() => {
    if (!connected || restored.current === queue) return;
    restored.current = queue;
    void queue.restore().catch(() => {
      if (restored.current === queue) restored.current = null;
      setError(
        "Cannot read saved files on this device. Reopen the board to try again.",
      );
    });
  }, [queue, connected]);
  useEffect(() => () => queue.dispose(), [queue]);
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (queue.hasUnsaved() || queue.isBusy()) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [queue]);
  function add(incoming: File[], point?: VecLike) {
    if (!editable.current) {
      setError("Reconnect with edit access before adding files.");
      return;
    }
    if (queue.isBusy()) {
      setError("Pause the current import before choosing more files.");
      return;
    }
    const files = incoming.map(normalizeBoardFile),
      base = imagePlacement(editor, point);
    if (pickerSlot.current.parent !== base.parentId)
      pickerSlot.current = { parent: base.parentId, next: 0 };
    const slot = point ? 0 : pickerSlot.current.next;
    const pending = entries.filter((e) => e.status !== "Added");
    const imageQueue = imageImports.get(editor),
      background = imageQueue?.pendingUsage() ?? { count: 0, bytes: 0 };
    if (
      background.count + pending.length + files.length > 50 ||
      pending.reduce((n, e) => n + e.size, 0) +
        files.reduce((n, f) => n + f.size, 0) +
        background.bytes >
        150 * 1024 * 1024
    ) {
      setError(
        "Keep pending files within 50 files and 150 MB. Remove failures or finish them first.",
      );
      return;
    }
    const documents: Array<{ entry: FileEntry; file: File }> = [];
    const nativeImages = files.filter(
      (file) => isPreviewImage(file) && file.size <= maxBytes && file.size > 0,
    );
    let imageIds: TLShapeId[] = [];
    try {
      if (nativeImages.length) {
        if (!imageQueue)
          throw new Error(
            "Image import is still starting. Try again in a moment.",
          );
        imageIds = imageQueue.add(nativeImages, point, base);
      }
    } catch (e) {
      setError((e as Error).message);
      return;
    }
    let documentIndex = 0;
    files.forEach((file) => {
      if (nativeImages.includes(file)) return;
      const index = documentIndex++;

      const placement = {
        parentId: base.parentId,
        center: {
          x:
            base.center.x +
            (nativeImages.length
              ? Math.min(5, Math.ceil(Math.sqrt(nativeImages.length))) * 344 +
                60
              : 0) +
            (index % 3) * 580 +
            ((slot % 6) * 32) / editor.getZoomLevel(),
          y:
            base.center.y +
            Math.floor(index / 3) * 760 +
            ((slot % 6) * 32) / editor.getZoomLevel(),
        },
      };
      documents.push({
        file,
        entry: {
          id: createShapeId(),
          name: file.name,
          size: file.size,
          mime: file.type,
          modified: file.lastModified,
          mode: choicesFor(file)[0][0],
          page: 1,
          worksheet: 1,
          headers: true,
          placement,
          status: "Ready",
        },
      });
    });
    try {
      queue.add(documents);
      setLastAdded(null);
      if (!point) pickerSlot.current.next += files.length;
      if (documents.length)
        void queue.run(documents.map(({ entry }) => entry.id));
      if (!documents.length && imageIds?.length) {
        editor.select(...imageIds);
      }
      setError("");
    } catch (e) {
      setError((e as Error).message);
    }
  }
  async function run() {
    if (!editable.current) {
      setError("Reconnect with edit access to resume.");
      return;
    }
    setError("");
    await queue.run();
  }
  return {
    entries,
    lastAdded,
    clearLastAdded: () => setLastAdded(null),
    error,
    setError,
    add,
    run,
    retry: (id: string) => queue.run([id]),
    busy: queue.isBusy(),
    pause: () => queue.pause(),
    configure: queue.configure.bind(queue),
    remove: async (id: string) => {
      try {
        await queue.remove(id);
      } catch {
        setError("Cannot remove the saved entry. Try again.");
      }
    },
    pending: entries.filter((e) => e.status !== "Added").length,
  };
}
